import React, { useEffect, useRef, useState } from "react";
import { Sparkles, X } from "lucide-react";

const SUPPORT_POPUP_LAST_SEEN_KEY = "jobzam_ai_job_board_support_popup_last_seen";
const SUPPORT_POPUP_INTERVAL_MS = 24 * 60 * 60 * 1000;
const SUPPORT_POPUP_DELAY_MS = 6000;

function canShowSupportPopup() {
  try {
    const lastSeen = Number(window.localStorage.getItem(SUPPORT_POPUP_LAST_SEEN_KEY));
    return !lastSeen || Date.now() - lastSeen >= SUPPORT_POPUP_INTERVAL_MS;
  } catch {
    return false;
  }
}

function markSupportPopupSeen() {
  try {
    window.localStorage.setItem(SUPPORT_POPUP_LAST_SEEN_KEY, String(Date.now()));
  } catch {
    // Ignore storage failures so the Jobs Portal stays usable.
  }
}

export default function AiJobBoardSupportPopup({ shouldPrompt, triggerKey }) {
  const [isVisible, setIsVisible] = useState(false);
  const warnedMissingLink = useRef(false);
  const supportUrl = process.env.REACT_APP_SUPPORT_CUSTOM_URL;

  useEffect(() => {
    if (!supportUrl) {
      if (process.env.NODE_ENV === "development" && !warnedMissingLink.current) {
        // eslint-disable-next-line no-console
        console.warn("JobZam support link is not configured.");
        warnedMissingLink.current = true;
      }
      setIsVisible(false);
      return undefined;
    }

    if (!shouldPrompt || !triggerKey || !canShowSupportPopup()) {
      setIsVisible(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      if (canShowSupportPopup()) {
        setIsVisible(true);
      }
    }, SUPPORT_POPUP_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [shouldPrompt, supportUrl, triggerKey]);

  if (!supportUrl || !isVisible) {
    return null;
  }

  const closePopup = () => {
    markSupportPopupSeen();
    setIsVisible(false);
  };

  const handleSupport = () => {
    markSupportPopupSeen();
    setIsVisible(false);
    window.open(supportUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <aside
      aria-label="Support the JobZam developers"
      className="fixed bottom-4 left-4 right-4 z-50 w-auto rounded-3xl border border-indigo-100 bg-white/95 p-4 shadow-[0_24px_80px_rgba(15,23,42,0.18)] backdrop-blur md:bottom-5 md:left-auto md:right-5 md:w-[330px]"
    >
      <button
        type="button"
        onClick={closePopup}
        aria-label="Close support prompt"
        className="absolute right-3 top-3 rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>

      <div className="flex gap-2.5 pr-7">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-600 to-blue-600 text-white shadow-lg shadow-indigo-500/20">
          <Sparkles className="h-4 w-4" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-sm font-extrabold text-slate-950">
            Support the JobZam developers 💙
          </h2>
          <p className="mt-1.5 text-[13px] leading-5 text-slate-600">
            JobZam is being bootstrapped by a small team building tools for job
            seekers. If the AI Job Board helped you find better job matches, you
            can support our development from &pound;3 and help us keep improving the
            platform.
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={handleSupport}
          className="rounded-2xl bg-gradient-to-r from-indigo-600 to-blue-600 px-3.5 py-2 text-xs font-bold text-white shadow-lg shadow-indigo-500/20 transition hover:-translate-y-0.5 hover:shadow-xl"
        >
          Support from &pound;3
        </button>
        <button
          type="button"
          onClick={closePopup}
          className="rounded-2xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-50"
        >
          Maybe later
        </button>
      </div>

      <p className="mt-2 text-[11px] font-semibold leading-4 text-slate-500">
        Optional contribution. The Jobs Portal remains available during beta.
      </p>
    </aside>
  );
}
